import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabaseWorker } from '../integrations/supabase/workerClient';
import { PostgrestError } from '@supabase/supabase-js';
import { TableName, DbRow, DbInsert, DbUpdate } from '../types/db';

// Generic hook for fetching a list of entities
export function useEntityList<T extends TableName, TClient>(
  tableName: T,
  dbToClientMapper: (dbEntity: DbRow<T>) => TClient
) {
  const { data, isLoading, error } = useQuery({
    queryKey: [tableName],
    queryFn: async () => {
      const { data, error } = await supabaseWorker.from(tableName as unknown as string).select('*');
      if (error) throw error;
      return data as unknown as DbRow<T>[];
    },
    select: (data) => data.map(dbToClientMapper),
  });

  return { entities: data, isLoading, error };
}

// Generic hook for CRUD operations on a single entity type
export function useEntityOperations<TClient, TData>(
  queryKey: TableName[],
  createFn: (entity: Partial<TClient>) => Promise<TData>,
  updateFn: (id: string, updates: Partial<TClient>) => Promise<TData>,
  deleteFn: (id: string) => Promise<void | unknown>
) {
  const queryClient = useQueryClient();

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  };

  const createEntity = useMutation<TData, PostgrestError, Partial<TClient>>({
    mutationFn: createFn,
    ...mutationOptions,
  });

  const updateEntity = useMutation<
    TData,
    PostgrestError,
    { id: string; updates: Partial<TClient> }
  >({
    mutationFn: ({ id, updates }) => updateFn(id, updates),
    ...mutationOptions,
  });

  const deleteEntity = useMutation<void | unknown, PostgrestError, string>({
    mutationFn: deleteFn,
    ...mutationOptions,
  });

  return {
    create: createEntity.mutateAsync,
    update: updateEntity.mutateAsync,
    remove: deleteEntity.mutateAsync,
  };
}
